// Feature 023 (US5) — the scorecard the learner is handed when they leave the table.
import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { SuitSymbol } from '../components/common/SuitSymbol';
import { BidText } from '../components/common/BidToken';
import { FAMILY_SHORT_TITLES } from '../config/moduleCatalog';
import type { ModuleFamily } from '../config/moduleCatalog';
import type { SessionOrigin } from '../types/problems';

/**
 * Session Summary — `/session-summary`.
 *
 * Drawn as a scorecard left on the table: the felt frame and brass rule of the practice page
 * the learner has just come from, sitting on the ordinary paper page. It reports the two
 * numbers they watched climb during the session (streak and score) plus the tallies behind
 * them.
 *
 * TWO SURFACES, ONE CARD. Module practice (`/practice/...`) and Bidding Practice (`/problems`,
 * and every mode it serves) both end here, discriminated by `kind` on the location state. They
 * differ in three places and nowhere else:
 *
 *   - the trail under the heading — a `family · topic · module` path for a module visit, the
 *     mode's own name for a problems visit (both taken verbatim from the masthead of the page
 *     being left, so the summary can never disagree with it);
 *   - where the two navigation controls lead;
 *   - the difficulty breakdown, which only a problems session produces.
 *
 * The data arrives entirely in router location state, because there is no server-side practice
 * session to re-read — the numbers exist only in the page being left, so they are handed over at
 * the moment of leaving. That makes the route unreachable on its own: a cold visit, a refresh or
 * a Back into it has no state and redirects. Which way it redirects is the ONE thing that cannot
 * come from the state, since by then there is none — hence `?from=problems` in the URL, whose
 * only job is to send a stranded problems learner back to `/problems` rather than to the module
 * hub.
 *
 * The header band is the practice masthead's twin (client request, 2026-07-29): the same
 * felt-deep strip under a brass rule, carrying the same trail the table's own banner shows. On a
 * phone it also borrows that masthead's shape — left aligned, tight padding, no ornament — so the
 * two pages read as one place and the path the learner took to get here is legible on the screen
 * where it is easiest to lose.
 *
 * The whole card is sized to be read without scrolling on every viewport in the e2e matrix
 * (e2e/helpers/problem-layout.ts), which is what the `short:` and `flat-ls:` trims below are
 * for. Three layouts, in order of how much room there is:
 *   - roomy: two stat rows under the heading, the two navigation buttons beneath the card;
 *   - `short:` (<= 750px tall): the same shape with the padding and type scaled down;
 *   - `flat-ls:` (landscape <= 450px tall): both stat rows fold onto ONE row and the
 *     navigation moves up into the card header as links, because 203px of usable height
 *     cannot hold a button row as well.
 *
 * Spec 031 (client request, 2026-10-05) added two things to that budget. On every phone, and on
 * landscape screens below 1024 wide, the trail moves up into the site header in place of the
 * brand, as on the practice pages (Header.tsx, sessionSummaryHeaderTitle), and the card's own
 * copy is hidden there (index.css). And the page is held to the viewport, so that where even the
 * trimmed card is too tall — an open ten-row difficulty breakdown on a landscape iPad — the
 * breakdown scrolls inside itself and the navigation stays on screen.
 */

/** What every session reports, whichever surface it was played on. */
interface SessionSummaryStats {
  /**
   * Optional since feature 028: a session that keeps no run omits it rather than reporting 0.
   * Both bidding surfaces keep one; Counting practice does not. The tile is left out entirely
   * when it is absent — the omit-don't-fabricate rule this card already applies to Best streak.
   */
  streak?: number;
  score: number;
  answered: number;
  correct: number;
  incorrect: number;
  skipped: number;
  bestStreak?: number;
}

export interface ModuleSessionSummaryState extends SessionSummaryStats {
  /** Optional so a history entry written before the two surfaces merged still reads as a module
   *  visit — that was the only kind there was. */
  kind?: 'module';
  family: ModuleFamily;
  topicKey: string;
  moduleSlug: string;
  /** Display names; both may carry bid tokens, so both render through `BidText`. */
  moduleTitle: string;
  topicTitle: string;
}

export interface ProblemsSessionSummaryState extends SessionSummaryStats {
  kind: 'problems';
  /**
   * What the session called itself, in the words the table's masthead used — "Beginner · Bidding
   * Practice", "Review Problems", "Overcalling". Passed in rather than derived here so the two
   * cannot drift: ProblemsPage owns that string (`modeTitle`), and it already folds in the level
   * a difficulty session was started at, which this page has no way to recover.
   */
  trail: string;
  /** Drives where "Start New Session" leads (FR-110 to FR-114). */
  sessionOrigin: SessionOrigin | null;
  correctByDifficulty: Record<number, number>;
  /** The bar chart's denominator — answered plus skipped, not just answered. */
  totalQuestions: number;
}

/**
 * A Counting practice visit (feature 028). Counting was the last caller of the pre-023 `/summary`
 * page; 023 recorded that it and Counting would "retire as one piece of work", and this is that.
 *
 * It measures less than the bidding surfaces do, and says so by omission rather than by zero:
 * no streak, no best streak, and no difficulty breakdown (counting hands carry no difficulty).
 */
export interface CountingSessionSummaryState extends SessionSummaryStats {
  kind: 'counting';
  /** What the session called itself in the table's masthead — "Counting". */
  trail: string;
  /** Drives where "Start New Session" leads; Counting is started from the dashboard door. */
  sessionOrigin: SessionOrigin | null;
  /** The bar chart's denominator — answered plus skipped. */
  totalQuestions: number;
}

export type SessionSummaryLocationState =
  | ModuleSessionSummaryState
  | ProblemsSessionSummaryState
  | CountingSessionSummaryState;

/**
 * Where "Start New Session" leads, per the origin the session was started from (FR-110 to
 * FR-114). Anything unlisted — including `difficulty-selector` itself and the deprecated
 * `component-selector` — falls through to the difficulty selector, which is where a learner
 * with no more specific mode belongs.
 *
 * A `<Link>` rather than the button-and-`navigate()` this replaces: every destination is a pure
 * function of the origin, and a control whose whole job is to go somewhere should be the element
 * that goes somewhere — it gets middle-click, "open in new tab" and the status-bar preview for
 * free, none of which a button can offer.
 */
const NEW_SESSION_DESTINATIONS: Partial<Record<SessionOrigin, string>> = {
  dashboard: '/dashboard',
  'important-sequences': '/problems?mode=important-sequences',
  'review-problems': '/problems?mode=review',
  // The competitive and Precision practice modes are all entered from the dashboard, so that is
  // where "again" means.
  overcalling: '/dashboard',
  '3rd-level-bid': '/dashboard',
  'practice-opening-bid': '/dashboard',
  'practice-responding-no-competition': '/dashboard',
  'practice-responding-with-competition': '/dashboard',
  'practice-responder-rebid-no-competition': '/dashboard',
  'practice-responder-rebid-with-competition': '/dashboard',
};

const TILE =
  'rounded-[10px] bg-cream px-4 py-4 text-center text-ink shadow-[0_2px_6px_rgba(0,0,0,.25)]' +
  ' short:py-2.5 phone-pt:px-1 flat-ls:px-2 flat-ls:py-1.5';
// 12px is the floor at every viewport, and phones drop the capitals (constitution VI —
// text-size floor / no all-caps on phones). The two rules pay for each other here: with four
// secondary tiles across a 360px screen each is only ~60px wide inside its padding, and
// sentence case is narrower per character than caps, so "Best streak" at 12px asks for less
// room than "BEST STREAK" did at 10px. The letter-spacing stays on wherever the capitals do —
// it was being dropped at exactly the size that needed it most.
const TILE_LABEL =
  'text-xs font-semibold uppercase tracking-[0.09em] text-ink-soft' +
  ' narrow:normal-case narrow:leading-tight narrow:tracking-normal';
// A reserved second label line for the four-across row. At 12px sentence case "Best streak"
// holds one line down to 360px — every viewport in the e2e matrix — but a 320px phone leaves
// each of these tiles only ~51px of inner width against a ~62px label, so it wraps there and
// the four values would otherwise sit on two different baselines. Reserving the line costs
// ~15px of card height, which the no-scroll budget has at every measured viewport. "Streak"
// and "Score" have a tile each half the width and never wrap, so they do not carry it.
const TILE_LABEL_TWO_LINE = ' phone-pt:min-h-[2.5em]';
const TILE_VALUE = 'mt-1 font-serif font-bold flat-ls:mt-0';

/** A cream stat panel on the felt. `size` is the only thing that separates the two tiers. */
function StatTile({
  label,
  value,
  size = 'primary',
  testId,
}: {
  label: string;
  value: React.ReactNode;
  size?: 'primary' | 'secondary';
  testId: string;
}) {
  return (
    <div className={TILE} data-testid={testId}>
      <dt className={TILE_LABEL + (size === 'secondary' ? TILE_LABEL_TWO_LINE : '')}>{label}</dt>
      <dd
        className={
          TILE_VALUE +
          (size === 'primary'
            ? ' text-4xl short:text-3xl flat-ls:text-xl'
            : ' text-2xl short:text-xl flat-ls:text-lg')
        }
      >
        {value}
      </dd>
    </div>
  );
}

/**
 * "Correct by difficulty level" — the client's specification, so it is never dropped, only
 * folded away where the card has no room for it.
 *
 * Open on a roomy viewport and closed below it, because the card's promise is that it can be
 * read without scrolling and up to ten level rows is more height than a phone or a short laptop
 * has left over. `<details>` rather than a bespoke toggle: it is the element for exactly this,
 * it is keyboard- and screen-reader-operable with no work, and Find-in-page opens it.
 *
 * The open state is derived from the media query on every change, so rotating a phone or
 * dragging a window across the threshold re-applies the rule rather than stranding the card in
 * whatever state the last viewport left it in. That does discard a manual toggle on resize,
 * which is the right trade: the viewport is the reason the default exists.
 */
function DifficultyBreakdown({
  correctByDifficulty,
  totalQuestions,
}: {
  correctByDifficulty: Record<number, number>;
  totalQuestions: number;
}) {
  // Both dimensions matter. Height is what the bar rows actually consume, but a narrow phone
  // that happens to be tall still cannot spare them beside everything else on the card, and
  // `narrow`/`short` are the two breakpoints the rest of this page is already written in.
  const roomy = useMediaQuery('(min-width: 768px) and (min-height: 751px)');
  const [open, setOpen] = useState(roomy);
  useEffect(() => setOpen(roomy), [roomy]);

  const levels = Object.entries(correctByDifficulty).sort(([a], [b]) => Number(a) - Number(b));
  if (levels.length === 0) return null;

  return (
    <details
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
      // The one part of the card that gives up height (spec 031, 2026-10-05): the page is held
      // to the viewport (index.css, `session-summary-page`), and where the card does not fit,
      // `min-h-0` lets this shrink and its rows scroll under the pinned heading, so the actions
      // below the card stay on screen. A card that fits is unchanged.
      className="mt-3 min-h-0 overflow-y-auto border-t border-brass/40 pt-3 short:mt-2 short:pt-2 flat-ls:mt-1.5 flat-ls:pt-1.5"
      data-testid="summary-difficulty"
    >
      {/* The native marker is kept: it is the disclosure affordance every browser's users
          already know, it rotates on open without a line of CSS, and replacing it with a glyph
          of our own would have to re-earn all of that. Pinned to the top of the scrolling rows
          on the card's own felt, so the control that folds them never scrolls away. A 24px line
          makes it a 24px-tall target (constitution VI); the text stays 12px. */}
      <summary className="sticky top-0 z-[1] cursor-pointer bg-felt text-xs leading-6 font-semibold uppercase tracking-[0.09em] text-brass-on-felt narrow:normal-case narrow:tracking-normal">
        Correct by difficulty level
      </summary>
      <div className="mt-2 space-y-1.5 short:mt-1.5 short:space-y-1">
        {levels.map(([level, count]) => (
          <div key={level} className="flex items-center gap-3" data-testid={`summary-difficulty-${level}`}>
            <span className="w-16 shrink-0 text-xs text-cream-soft">Level {level}</span>
            {/* Brass on felt-deep. The old page's green-on-cream bar is invisible here — the
                fill and the card would be the same colour family. */}
            <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-felt-deep">
              <div
                className="h-full rounded-full bg-brass"
                style={{ width: `${Math.min(100, (count / Math.max(1, totalQuestions)) * 100)}%` }}
              />
            </div>
            <span className="w-6 shrink-0 text-right text-xs font-semibold text-cream">{count}</span>
          </div>
        ))}
      </div>
    </details>
  );
}

export function SessionSummaryPage() {
  usePageTitle('Session Summary — Hawkes Bridge');
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const state = location.state as SessionSummaryLocationState | null;

  // No state means the page was reached other than by leaving a table — there is nothing to
  // report. Where to send them is the one decision the (absent) state cannot inform, so it rides
  // in the URL, which a refresh preserves.
  if (!state) {
    return <Navigate to={searchParams.get('from') === 'problems' ? '/problems' : '/dashboard'} replace />;
  }

  const isProblems = state.kind === 'problems';
  const isCounting = state.kind === 'counting';
  // Both non-module variants own a `trail` and a `sessionOrigin` and go back where the session
  // was started, rather than to a module page they do not have.
  const isModule = !isProblems && !isCounting;

  // A module visit with nothing answered has no session to summarise and no module page of its
  // own to offer, so it goes back to the dashboard. A problems or counting visit stays: ending a
  // session without answering is an ordinary thing to do there — the leave control is on screen
  // from the first hand — and the learner is told so below rather than being bounced.
  if (isModule && !(state.answered > 0)) {
    return <Navigate to="/dashboard" replace />;
  }

  const { streak, score, answered, correct, incorrect, skipped, bestStreak } = state;

  // Omit-don't-fabricate: a session that measured no run leaves the tile out rather than
  // printing a zero, which would read as "your best run was nothing" instead of "not measured".
  const hasStreak = typeof streak === 'number';
  const hasBestStreak = typeof bestStreak === 'number';

  // Guarded despite the `answered > 0` gate above: the gate is the caller's promise, this is
  // the page's own defence against dividing by zero — and the problems variant is not gated at
  // all, so here it is load-bearing rather than belt-and-braces.
  const accuracy = answered > 0 ? Math.round((correct / answered) * 100) : 0;

  const primaryAction = isProblems
    ? {
        to: NEW_SESSION_DESTINATIONS[state.sessionOrigin ?? 'difficulty-selector'] ?? '/problems',
        label: 'Start New Session',
      }
    : isCounting
    ? {
        // Counting has no linkable URL of its own — the page expects its hands in router state —
        // so the way back is the dashboard door that starts one, not the session route.
        to: '/dashboard',
        label: 'Back to the dashboard',
      }
    : {
        to: `/inventory/${state.family}/${state.topicKey}`,
        label: 'Back to modules',
      };

  return (
    <div
      className="mx-auto flex min-h-0 w-full max-w-[720px] flex-col px-4 pt-8 pb-4 sm:pt-12 sm:pb-6 short:py-4 flat-ls:py-2"
      data-testid="session-summary"
    >
      {/* The scorecard: the table's own felt and brass, lifted off the page. */}
      <div className="flex min-h-0 flex-col overflow-hidden rounded-[14px] bg-felt shadow-[0_12px_40px_rgba(0,0,0,.4)]">
        {/* `suit-on-dark` sits on the whole band, not just the decorative row: the module and
            topic names below carry bid tokens, and a black club glyph on felt-deep is all but
            invisible. Same reason the practice masthead carries it. */}
        <div
          className={
            'suit-on-dark border-b-2 border-brass bg-felt-deep px-5 py-4 text-center sm:border-b-[3px]' +
            ' short:py-2.5 phone-pt:px-2.5 phone-pt:py-1.5 phone-pt:text-left' +
            ' flat-ls:flex flat-ls:items-center flat-ls:justify-between flat-ls:gap-4' +
            ' flat-ls:px-3 flat-ls:py-1.5 flat-ls:text-left'
          }
        >
          <div className="min-w-0">
            {/* Decorative only — the suits say nothing the heading does not, so they are hidden
                from assistive tech rather than read out as "spades hearts diamonds clubs". The
                first thing to go when the viewport is short, being pure ornament — and absent on
                a phone whatever its height, since the masthead this band now imitates carries no
                ornament at all. */}
            <span
              className="mb-1 flex justify-center gap-2 text-lg tracking-widest short:hidden phone-pt:hidden"
              aria-hidden="true"
            >
              <SuitSymbol suit="S" className="suit-glyph-inherit" />
              <SuitSymbol suit="H" className="suit-glyph-inherit" />
              <SuitSymbol suit="D" className="suit-glyph-inherit" />
              <SuitSymbol suit="C" className="suit-glyph-inherit" />
            </span>
            <h1 className="font-serif text-2xl font-bold text-cream short:text-xl phone-pt:text-xl flat-ls:text-lg">
              Session Summary
            </h1>
            {/* The trail, in the practice page's own words and order. A module visit spells out
                `family · topic · module`: it used to read `module · topic` with no root, which
                named the same two things but backwards for anything trying to be a path — the
                learner arrives here off a page whose banner IS this trail, and the two have to
                agree or neither reads as one. The root is the FAMILY, not the bare word
                "Practice" (client request, 2026-07-29): both families are practised the same way,
                so "Practice" was the one segment that never told the learner anything.
                FAMILY_SHORT_TITLES is the same map the phone banner reads (Header.tsx), so the
                two can never drift.

                A problems visit has no such hierarchy — one mode, no course above it — so it
                carries the mode's own name, handed over by the page that just displayed it. */}
            <p
              className={
                'mt-1 text-[13px] text-cream-soft short:mt-0.5' +
                // 12px is the floor for the trail, not the 11px the tile eyebrows sit at
                // (client decision, 2026-07-29): those are one-word labels read beside the number
                // they belong to, whereas this is a sentence-length path and the only thing on
                // the page naming the module. It wraps to two lines on a ~360px phone at this
                // size, which the no-scroll budget has the room for — measured across the matrix.
                ' phone-pt:mt-0.5 phone-pt:text-xs phone-pt:leading-snug' +
                // Two lines, not one: `truncate` cut the module name mid-word on the narrowest
                // landscape phone (640x360, measured) once the root became a family name, and the
                // module is the segment the learner is least able to guess. `line-clamp-2` still
                // caps the growth, so a long trail can never push the tiles off a 303px screen.
                ' flat-ls:mt-0 flat-ls:line-clamp-2 flat-ls:text-xs'
              }
              data-testid="summary-trail"
            >
              {isModule ? (
                <>
                  {FAMILY_SHORT_TITLES[state.family]}
                  {' · '}
                  <BidText text={state.topicTitle} />
                  {' · '}
                  <BidText text={state.moduleTitle} />
                </>
              ) : (
                // Problems and Counting both name themselves in the words their masthead used.
                <BidText text={state.trail} />
              )}
            </p>
          </div>

          {/* Flat landscape has no room for the button row under the card, so the same two
              destinations ride up here as links — the shape the practice masthead already
              uses for "Back to modules". Exactly one of the two navigations is ever in the
              accessibility tree, since the other is display:none. */}
          <div
            className="hidden shrink-0 items-center gap-4 text-sm flat-ls:flex"
            data-testid="summary-nav-top"
          >
            <Link
              to={primaryAction.to}
              aria-label={primaryAction.label}
              className="inline-flex min-h-[24px] items-center whitespace-nowrap font-semibold text-cream no-underline hover:underline"
            >
              {isProblems ? primaryAction.label : `← ${primaryAction.label}`}
            </Link>
            <Link
              to="/dashboard"
              className="inline-flex min-h-[24px] items-center whitespace-nowrap font-semibold text-brass-on-felt no-underline hover:underline"
            >
              Dashboard
            </Link>
          </div>
        </div>

        <div className="flex min-h-0 flex-col px-4 py-5 sm:px-6 sm:py-6 short:py-3 flat-ls:px-3 flat-ls:py-2">
          {/* A problems session can be ended before a single hand is answered — the End Session
              button is on screen from the first one — and a card of zeroes with no explanation
              reads as a bug rather than as a choice the learner made. */}
          {answered === 0 && (
            <p
              className="mb-3 rounded-md bg-felt-deep px-3 py-2 text-center text-xs text-cream-soft short:mb-2 flat-ls:mb-1.5"
              data-testid="summary-no-answers"
            >
              You ended the session without answering any problems.
            </p>
          )}

          {/* Flat landscape folds the two tiers into a single row; everywhere else this
              wrapper is an ordinary block and the tiers stack. */}
          <div className="flat-ls:flex flat-ls:items-stretch flat-ls:gap-3">
            {/* Two lists rather than one: a `dl` may only contain dt/dd pairs (optionally each
                wrapped in a single div, which is the tile), so the grid has to BE the list — a
                grid div inside one `dl` would orphan the pairs two levels down. */}
            {/* The headline pair stays paired at every width — both values are two characters
                at most, and separating them would cost the side-by-side reading that makes the
                streak and the score legible as two different measures of the same session. */}
            {/* One column when there is no streak to pair the score with (Counting), two when
                there is. A single tile stretched across a two-column grid reads as a missing
                tile rather than as a session that measures one thing fewer. */}
            <dl className={`grid gap-3 flat-ls:flex-[2] flat-ls:gap-2 ${
              hasStreak ? 'grid-cols-2' : 'grid-cols-1'
            }`}>
              {hasStreak && (
                <StatTile
                  label="Streak"
                  testId="summary-streak"
                  value={
                    <>
                      {streak}
                      {/* The flame marks a run that is going the learner's way; a losing run gets
                          the number alone. Same rule as the table's status line. */}
                      {streak > 0 && <span aria-hidden="true"> 🔥</span>}
                    </>
                  }
                />
              )}
              <StatTile
                label="Score"
                testId="summary-score"
                value={score > 0 ? `+${score}` : String(score)}
              />
            </dl>

            <div className="mx-auto mt-5 h-0.5 w-16 bg-brass short:mt-3 flat-ls:hidden" aria-hidden="true" />

            {/* All four across even on a phone: stacked, or even two-by-two, these tiles cost
                more height than the whole card can spare on a 547-high iPhone SE. The labels
                lose their letter spacing and drop a point at that width so they still hold one
                line, and the gap tightens to buy them the room. */}
            <dl className={`mt-5 grid gap-3 short:mt-3 phone-pt:gap-1.5 flat-ls:mt-0 flat-ls:flex-[4] flat-ls:gap-2 ${
              hasBestStreak ? 'grid-cols-4' : 'grid-cols-3'
            }`}>
              {hasBestStreak && (
                <StatTile
                  label="Best streak"
                  testId="summary-best-streak"
                  value={bestStreak}
                  size="secondary"
                />
              )}
              <StatTile
                label="Accuracy"
                testId="summary-accuracy"
                value={`${accuracy}%`}
                size="secondary"
              />
              <StatTile
                label="Answered"
                testId="summary-answered"
                value={answered}
                size="secondary"
              />
              <StatTile
                label="Skipped"
                testId="summary-skipped"
                value={skipped}
                size="secondary"
              />
            </dl>
          </div>

          {/* The right/wrong split reads as a caption rather than a fourth tile: inside one it
              forced that tile taller than its neighbours at every width. */}
          <p
            className="mt-3 text-center text-xs text-cream-soft short:mt-2 flat-ls:mt-1.5"
            data-testid="summary-split"
          >
            {correct} right · {incorrect} wrong
          </p>

          {isProblems && (
            <DifficultyBreakdown
              correctByDifficulty={state.correctByDifficulty}
              totalQuestions={state.totalQuestions}
            />
          )}
        </div>
      </div>

      {/* The way onward sits on the paper, off the card — the scorecard is the record, these
          are the next moves. Replaced by the header links when the viewport is too flat. */}
      <div
        className="mt-6 flex flex-wrap justify-center gap-3 short:mt-3 flat-ls:hidden"
        data-testid="summary-nav-bottom"
      >
        <Link to={primaryAction.to} className="btn-primary no-underline">
          {primaryAction.label}
        </Link>
        <Link to="/dashboard" className="btn-secondary no-underline">
          Dashboard
        </Link>
      </div>
    </div>
  );
}
